import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Stack, TextField, Typography,
} from "@mui/material";
import { namespace } from "@common-config/constants";
import {
    useGetWithdrawalContextQuery,
    useSubmitWithdrawalMutation,
} from "@common-reducers/backend-api/backend-api";
import {
    formatWithdrawalInstant,
    shouldShowWithdrawal,
    validateWithdrawalForm,
    WithdrawalContext,
    WithdrawalFormError,
    WithdrawalResult,
} from "@common/models/withdrawal";

type Step = "form" | "confirm" | "done";

interface WithdrawalDialogProps {
    context: WithdrawalContext;
    open: boolean;
    onClose: () => void;
}

/** Art 11a: statement form, then a separate confirm step. No offers, no reasons, no retention. */
const WithdrawalDialog = ({ context, open, onClose }: WithdrawalDialogProps) => {
    const { t, i18n } = useTranslation(namespace);
    const [submitWithdrawal, { isLoading }] = useSubmitWithdrawalMutation();
    const [step, setStep] = useState<Step>("form");
    const [name, setName] = useState(context.name);
    const [email, setEmail] = useState(context.email);
    const [selected, setSelected] = useState<string[]>(context.contracts.map((c) => c.id));
    const [error, setError] = useState<WithdrawalFormError | null>(null);
    const [failed, setFailed] = useState(false);
    const [result, setResult] = useState<WithdrawalResult | null>(null);

    const toggle = (id: string) =>
        setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const next = () => {
        const problem = validateWithdrawalForm({ name, email, subscriptionIds: selected });
        setError(problem);
        if (!problem) setStep("confirm");
    };

    const confirm = async () => {
        setFailed(false);
        try {
            const res = await submitWithdrawal({ name: name.trim(), email: email.trim(), subscriptionIds: selected }).unwrap();
            setResult(res);
            setStep("done");
        } catch {
            setFailed(true);
        }
    };

    const chosen = context.contracts.filter((c) => selected.includes(c.id));

    return (
        <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{step === "done" ? t("withdrawal.doneTitle") : t("withdrawal.dialogTitle")}</DialogTitle>
            <DialogContent>
                {step === "form" && (
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography variant="body2">{t("withdrawal.formIntro")}</Typography>
                        <TextField label={t("withdrawal.name")} value={name} onChange={(e) => setName(e.target.value)}
                            error={error === "name"} helperText={error === "name" ? t("withdrawal.errorName") : undefined} fullWidth />
                        <TextField label={t("withdrawal.email")} value={email} onChange={(e) => setEmail(e.target.value)}
                            error={error === "email"} helperText={error === "email" ? t("withdrawal.errorEmail") : undefined} fullWidth />
                        <Box>
                            <Typography variant="subtitle2">{t("withdrawal.contracts")}</Typography>
                            {context.contracts.map((c) => (
                                <FormControlLabel key={c.id}
                                    control={<Checkbox checked={selected.includes(c.id)} onChange={() => toggle(c.id)} />}
                                    label={`${c.planName ?? c.id} (${c.id})`} />
                            ))}
                            {error === "contracts" && <Typography color="error" variant="caption">{t("withdrawal.errorContracts")}</Typography>}
                        </Box>
                    </Stack>
                )}
                {step === "confirm" && (
                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                        <Typography variant="body2">{t("withdrawal.confirmIntro")}</Typography>
                        {chosen.map((c) => (
                            <Typography key={c.id} variant="body2" fontWeight={600}>{c.planName ?? c.id} ({c.id})</Typography>
                        ))}
                        <Typography variant="body2">{name.trim()} · {email.trim()}</Typography>
                        <Typography variant="body2">{chosen.some((c) => c.charged) ? t("withdrawal.confirmNext") : t("withdrawal.confirmNextNoCharge")}</Typography>
                        {failed && <Typography color="error" variant="body2">{t("withdrawal.errorGeneric", { supportEmail: context.supportEmail })}</Typography>}
                    </Stack>
                )}
                {step === "done" && result && (
                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                        <Typography variant="body2">{t("withdrawal.doneAt", { date: formatWithdrawalInstant(result.submittedAt, i18n.language) })}</Typography>
                        <Typography variant="body2">{result.cancelledAll ? t("withdrawal.doneCancelled") : t("withdrawal.doneCancelFailed")}</Typography>
                        <Typography variant="body2">
                            {result.receiptQueued
                                ? t("withdrawal.doneReceipt", { email: email.trim(), supportEmail: result.supportEmail, reference: result.statementId })
                                : t("withdrawal.doneReceiptFailed", { reference: result.statementId })}
                        </Typography>
                        <Typography variant="body2">{result.anyCharged ? t("withdrawal.doneRefund", { supportEmail: result.supportEmail }) : t("withdrawal.doneNoCharge", { supportEmail: result.supportEmail })}</Typography>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {step === "form" && (<>
                    <Button onClick={onClose}>{t("withdrawal.cancel")}</Button>
                    <Button variant="contained" onClick={next}>{t("withdrawal.next")}</Button>
                </>)}
                {step === "confirm" && (<>
                    <Button onClick={() => setStep("form")} disabled={isLoading}>{t("withdrawal.back")}</Button>
                    <Button variant="contained" color="error" onClick={confirm} disabled={isLoading}>{t("withdrawal.confirmButton")}</Button>
                </>)}
                {step === "done" && <Button variant="contained" onClick={onClose}>{t("withdrawal.close")}</Button>}
            </DialogActions>
        </Dialog>
    );
};

/** Always-visible entry on the Buy Subscription page. Renders nothing unless the server flag is on and a contract exists. */
export const WithdrawalSection = () => {
    const { t } = useTranslation(namespace);
    const { data } = useGetWithdrawalContextQuery();
    const [open, setOpen] = useState(false);
    if (!shouldShowWithdrawal(data)) return null;
    return (
        <Box data-testid="withdrawal-section" sx={{ mt: 4, mb: 2, textAlign: "center" }}>
            <Typography variant="body2" sx={{ mb: 1 }}>{t("withdrawal.sectionHint")}</Typography>
            {data.canWithdraw
                ? <Button variant="outlined" onClick={() => setOpen(true)}>{t("withdrawal.openButton")}</Button>
                : <Typography variant="body2" fontWeight={600}>
                    {data.ownerEmail
                        ? t("withdrawal.ownerOnly", { ownerEmail: data.ownerEmail })
                        : t("withdrawal.ownerOnlyNoEmail", { supportEmail: data.supportEmail })}
                </Typography>}
            {open && data.canWithdraw && <WithdrawalDialog context={data} open={open} onClose={() => setOpen(false)} />}
        </Box>
    );
};

export default WithdrawalSection;
